/**
 * Push Notifications Service
 * 
 * Provides browser push notifications for important CRM events.
 * Works with Service Worker for background notifications.
 * Integrates with Supabase Realtime for live updates.
 * 
 * Features:
 * - Browser Notification API integration
 * - Permission management
 * - Event-based notifications (candidates, tasks, appointments, emails)
 * - Notification grouping and throttling
 * - Sound alerts
 * - Action buttons
 * - Badge updates
 */

// ========================
// TYPES
// ========================

export type NotificationPriority = 'low' | 'medium' | 'high' | 'urgent';
export type NotificationCategory = 
  | 'candidate' 
  | 'task' 
  | 'appointment' 
  | 'email' 
  | 'message' 
  | 'activity'
  | 'system'
  | 'alert';

export interface PushNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  category: NotificationCategory;
  priority: NotificationPriority;
  data?: Record<string, unknown>;
  actions?: Array<{
    action: string;
    title: string;
    icon?: string;
  }>;
  silent?: boolean;
  requireInteraction?: boolean;
  renotify?: boolean;
  vibrate?: number[];
  timestamp?: number;
  onClick?: () => void;
}

export interface NotificationPreferences {
  enabled: boolean;
  categories: {
    candidate: boolean;
    task: boolean;
    appointment: boolean;
    email: boolean;
    message: boolean;
    activity: boolean;
    system: boolean;
    alert: boolean;
  };
  priorities: {
    low: boolean;
    medium: boolean;
    high: boolean;
    urgent: boolean;
  };
  sound: boolean;
  vibrate: boolean;
  quietHours: {
    enabled: boolean;
    start: string; // HH:MM
    end: string;   // HH:MM
  };
}

// ========================
// DEFAULT PREFERENCES
// ========================

const DEFAULT_PREFERENCES: NotificationPreferences = {
  enabled: true,
  categories: {
    candidate: true,
    task: true,
    appointment: true,
    email: true,
    message: true,
    activity: false, // Activity updates are less important
    system: true,
    alert: true,
  },
  priorities: {
    low: false,
    medium: true,
    high: true,
    urgent: true,
  },
  sound: true,
  vibrate: true,
  quietHours: {
    enabled: false,
    start: '22:00',
    end: '07:00',
  },
};

// ========================
// NOTIFICATION ICONS
// ========================

const CATEGORY_ICONS: Record<NotificationCategory, string> = {
  candidate: '👤',
  task: '✅',
  appointment: '📅',
  email: '📧',
  message: '💬',
  activity: '📊',
  system: '⚙️',
  alert: '🚨',
};

const _PRIORITY_COLORS: Record<NotificationPriority, string> = {
  low: '#6b7280',
  medium: '#3b82f6',
  high: '#f59e0b',
  urgent: '#ef4444',
};

// ========================
// PUSH NOTIFICATION SERVICE
// ========================

class PushNotificationService {
  private static instance: PushNotificationService;
  private preferences: NotificationPreferences;
  private notificationQueue: Map<string, NodeJS.Timeout> = new Map();
  private recentNotifications: Map<string, number> = new Map();
  private audioContext: AudioContext | null = null;
  private clickHandlers: Map<string, () => void> = new Map();

  // Throttle settings
  private readonly THROTTLE_WINDOW = 5000; // 5 seconds
  private readonly MAX_NOTIFICATIONS_PER_WINDOW = 3;

  private constructor() {
    this.preferences = this.loadPreferences();
    this.initializeAudio();
    this.setupNotificationListeners();
    console.log('🔔 Push Notification Service initialized');
  }

  static getInstance(): PushNotificationService {
    if (!this.instance) {
      this.instance = new PushNotificationService();
    }
    return this.instance;
  }

  // ========================
  // PERMISSION MANAGEMENT
  // ========================

  /**
   * Check if notifications are supported
   */
  isSupported(): boolean {
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) return 'denied';
    return Notification.permission;
  }

  /**
   * Request notification permission from user
   */
  async requestPermission(): Promise<boolean> {
    if (!this.isSupported()) {
      console.warn('🔔 Browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      console.log('🔔 Notification permission already granted');
      return true;
    }

    if (Notification.permission === 'denied') {
      console.warn('🔔 Notification permission was denied');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      const granted = permission === 'granted';
      console.log(`🔔 Notification permission: ${permission}`);
      return granted;
    } catch (error) {
      console.error('🔔 Error requesting notification permission:', error);
      return false;
    }
  }

  // ========================
  // PREFERENCES MANAGEMENT
  // ========================

  /**
   * Load preferences from localStorage
   */
  private loadPreferences(): NotificationPreferences {
    try {
      const stored = localStorage.getItem('push_notification_preferences');
      if (stored) {
        return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
      }
    } catch (error) {
      console.warn('🔔 Could not load notification preferences:', error);
    }
    return DEFAULT_PREFERENCES;
  }

  /**
   * Save preferences to localStorage
   */
  private savePreferences(): void {
    try {
      localStorage.setItem('push_notification_preferences', JSON.stringify(this.preferences));
    } catch (error) {
      console.warn('🔔 Could not save notification preferences:', error);
    }
  }

  /**
   * Get current preferences
   */
  getPreferences(): NotificationPreferences {
    return { ...this.preferences };
  }

  /**
   * Update preferences
   */
  updatePreferences(updates: Partial<NotificationPreferences>): void {
    this.preferences = { ...this.preferences, ...updates };
    this.savePreferences();
    console.log('🔔 Notification preferences updated');
  }

  /**
   * Enable/disable a specific category
   */
  setCategoryEnabled(category: NotificationCategory, enabled: boolean): void {
    this.preferences.categories[category] = enabled;
    this.savePreferences();
  }

  // ========================
  // QUIET HOURS
  // ========================

  /**
   * Check if currently in quiet hours
   */
  private isInQuietHours(): boolean {
    if (!this.preferences.quietHours.enabled) return false;

    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const [startHour, startMin] = this.preferences.quietHours.start.split(':').map(Number);
    const [endHour, endMin] = this.preferences.quietHours.end.split(':').map(Number);

    const startTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;

    // Handle overnight quiet hours (e.g., 22:00 - 07:00)
    if (startTime > endTime) {
      return currentTime >= startTime || currentTime <= endTime;
    }

    return currentTime >= startTime && currentTime <= endTime;
  }

  // ========================
  // THROTTLING
  // ========================

  /**
   * Check if notification should be throttled
   */
  private shouldThrottle(_tag: string): boolean {
    const now = Date.now();
    const recentCount = Array.from(this.recentNotifications.values())
      .filter(time => now - time < this.THROTTLE_WINDOW).length;

    return recentCount >= this.MAX_NOTIFICATIONS_PER_WINDOW;
  }

  /**
   * Record notification for throttling
   */
  private recordNotification(tag: string): void {
    this.recentNotifications.set(tag, Date.now());

    // Cleanup old entries
    const now = Date.now();
    const keysToDelete: string[] = [];
    this.recentNotifications.forEach((time, key) => {
      if (now - time > this.THROTTLE_WINDOW * 2) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach(key => this.recentNotifications.delete(key));
  }

  // ========================
  // AUDIO
  // ========================

  /**
   * Initialize audio context for notification sounds
   */
  private initializeAudio(): void {
    try {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    } catch (error) {
      console.warn('🔔 Could not initialize audio context:', error);
    }
  }

  /**
   * Play notification sound
   */
  private playSound(priority: NotificationPriority): void {
    if (!this.preferences.sound || !this.audioContext) return;

    try {
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);

      // Different frequencies for different priorities
      const frequencies: Record<NotificationPriority, number> = {
        low: 440,      // A4
        medium: 523,   // C5
        high: 659,     // E5
        urgent: 880,   // A5
      };

      oscillator.frequency.value = frequencies[priority];
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, this.audioContext.currentTime + 0.3);

      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.3);
    } catch (error) {
      console.warn('🔔 Could not play notification sound:', error);
    }
  }

  // ========================
  // NOTIFICATION LISTENERS
  // ========================

  /**
   * Setup global notification click listeners
   */
  private setupNotificationListeners(): void {
    // Note: In a real app, you'd handle this in a Service Worker
    // This is a simplified version for browser notifications
  }

  // ========================
  // CORE NOTIFICATION METHOD
  // ========================

  /**
   * Show a push notification
   */
  async notify(options: PushNotificationOptions): Promise<boolean> {
    // Check if enabled
    if (!this.preferences.enabled) {
      console.log('🔔 Notifications are disabled');
      return false;
    }

    // Check permission
    if (Notification.permission !== 'granted') {
      console.log('🔔 Notification permission not granted');
      return false;
    }

    // Check category preference
    if (!this.preferences.categories[options.category]) {
      console.log(`🔔 Category ${options.category} is disabled`);
      return false;
    }

    // Check priority preference
    if (!this.preferences.priorities[options.priority]) {
      console.log(`🔔 Priority ${options.priority} is disabled`);
      return false;
    }

    // Check quiet hours (except for urgent notifications)
    if (options.priority !== 'urgent' && this.isInQuietHours()) {
      console.log('🔔 In quiet hours, notification suppressed');
      return false;
    }

    // Generate tag for deduplication
    const tag = options.tag || `${options.category}-${Date.now()}`;

    // Check throttling (except for urgent notifications)
    if (options.priority !== 'urgent' && this.shouldThrottle(tag)) {
      console.log('🔔 Notification throttled');
      return false;
    }

    try {
      // Create notification
      const notification = new Notification(
        `${CATEGORY_ICONS[options.category]} ${options.title}`,
        {
          body: options.body,
          icon: options.icon || '/favicon.ico',
          badge: options.badge || '/favicon.ico',
          // image: options.image, // Not supported in all browsers
          tag,
          data: {
            ...options.data,
            category: options.category,
            priority: options.priority,
            timestamp: options.timestamp || Date.now(),
          },
          silent: options.silent || false,
          requireInteraction: options.requireInteraction || options.priority === 'urgent',
          // Note: vibrate and timestamp are not in standard NotificationOptions type
        }
      );

      // Handle click
      notification.onclick = () => {
        window.focus();
        notification.close();
        
        // Call custom click handler if provided
        if (options.onClick) {
          options.onClick();
        } else if (this.clickHandlers.has(tag)) {
          this.clickHandlers.get(tag)!();
          this.clickHandlers.delete(tag);
        }
      };

      // Store click handler
      if (options.onClick) {
        this.clickHandlers.set(tag, options.onClick);
      }

      // Play sound
      if (!options.silent) {
        this.playSound(options.priority);
      }

      // Record for throttling
      this.recordNotification(tag);

      console.log(`🔔 Notification shown: ${options.title}`);
      return true;
    } catch (error) {
      console.error('🔔 Error showing notification:', error);
      return false;
    }
  }

  // ========================
  // CONVENIENCE METHODS
  // ========================

  /**
   * Notify about a new candidate
   */
  async notifyNewCandidate(
    candidateName: string,
    position: string,
    matchScore?: number,
    candidateId?: string
  ): Promise<boolean> {
    const priority: NotificationPriority = 
      matchScore && matchScore >= 90 ? 'high' : 
      matchScore && matchScore >= 70 ? 'medium' : 'low';

    return this.notify({
      title: 'Neuer Kandidat',
      body: `${candidateName} - ${position}${matchScore ? ` (${matchScore}% Match)` : ''}`,
      category: 'candidate',
      priority,
      tag: `candidate-new-${candidateId || Date.now()}`,
      data: { candidateId, matchScore },
      onClick: () => {
        if (candidateId) {
          window.location.href = `/candidates/${candidateId}`;
        }
      },
    });
  }

  /**
   * Notify about candidate status change
   */
  async notifyCandidateStatusChange(
    candidateName: string,
    oldStatus: string,
    newStatus: string,
    candidateId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Kandidaten-Status geändert',
      body: `${candidateName}: ${oldStatus} → ${newStatus}`,
      category: 'candidate',
      priority: 'medium',
      tag: `candidate-status-${candidateId || Date.now()}`,
      data: { candidateId, oldStatus, newStatus },
    });
  }

  /**
   * Notify about a new task
   */
  async notifyNewTask(
    taskTitle: string,
    assignedBy?: string,
    taskId?: string,
    dueDate?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Neue Aufgabe zugewiesen',
      body: `${taskTitle}${assignedBy ? ` von ${assignedBy}` : ''}${dueDate ? ` (Fällig: ${dueDate})` : ''}`,
      category: 'task',
      priority: 'medium',
      tag: `task-new-${taskId || Date.now()}`,
      data: { taskId, dueDate },
      onClick: () => {
        if (taskId) {
          window.location.href = `/tasks/${taskId}`;
        }
      },
    });
  }

  /**
   * Notify about task deadline
   */
  async notifyTaskDeadline(
    taskTitle: string,
    timeLeft: string,
    taskId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Aufgaben-Deadline',
      body: `"${taskTitle}" - ${timeLeft}`,
      category: 'task',
      priority: 'high',
      tag: `task-deadline-${taskId || Date.now()}`,
      requireInteraction: true,
      data: { taskId },
    });
  }

  /**
   * Notify about task completion
   */
  async notifyTaskCompleted(
    taskTitle: string,
    completedBy: string,
    taskId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Aufgabe erledigt',
      body: `"${taskTitle}" wurde von ${completedBy} abgeschlossen`,
      category: 'task',
      priority: 'low',
      tag: `task-completed-${taskId || Date.now()}`,
      data: { taskId, completedBy },
    });
  }

  /**
   * Notify about upcoming appointment (15 min reminder)
   */
  async notifyAppointmentReminder(
    appointmentTitle: string,
    startTime: string,
    location?: string,
    appointmentId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Termin in 15 Minuten',
      body: `${appointmentTitle} um ${startTime}${location ? ` - ${location}` : ''}`,
      category: 'appointment',
      priority: 'high',
      tag: `appointment-reminder-${appointmentId || Date.now()}`,
      requireInteraction: true,
      data: { appointmentId, startTime, location },
      onClick: () => {
        window.location.href = '/calendar';
      },
    });
  }

  /**
   * Notify about new appointment
   */
  async notifyNewAppointment(
    appointmentTitle: string,
    date: string,
    startTime: string,
    createdBy?: string,
    appointmentId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Neuer Termin',
      body: `${appointmentTitle} am ${date} um ${startTime}${createdBy ? ` (von ${createdBy})` : ''}`,
      category: 'appointment',
      priority: 'medium',
      tag: `appointment-new-${appointmentId || Date.now()}`,
      data: { appointmentId, date, startTime },
    });
  }

  /**
   * Notify about appointment cancellation
   */
  async notifyAppointmentCancelled(
    appointmentTitle: string,
    date: string,
    cancelledBy?: string,
    appointmentId?: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Termin abgesagt',
      body: `${appointmentTitle} am ${date}${cancelledBy ? ` wurde von ${cancelledBy} abgesagt` : ' wurde abgesagt'}`,
      category: 'appointment',
      priority: 'high',
      tag: `appointment-cancelled-${appointmentId || Date.now()}`,
      data: { appointmentId, date, cancelledBy },
    });
  }

  /**
   * Notify about new email
   */
  async notifyNewEmail(
    sender: string,
    subject: string,
    emailId?: string
  ): Promise<boolean> {
    return this.notify({
      title: `E-Mail von ${sender}`,
      body: subject || '(Kein Betreff)',
      category: 'email',
      priority: 'medium',
      tag: `email-new-${emailId || Date.now()}`,
      data: { emailId, sender },
      onClick: () => {
        window.location.href = '/email';
      },
    });
  }

  /**
   * Notify about new chat message
   */
  async notifyNewMessage(
    sender: string,
    message: string,
    conversationId?: string
  ): Promise<boolean> {
    return this.notify({
      title: `Nachricht von ${sender}`,
      body: message.length > 100 ? `${message.substring(0, 100)}...` : message,
      category: 'message',
      priority: 'medium',
      tag: `message-${conversationId || Date.now()}`,
      data: { conversationId, sender },
    });
  }

  /**
   * Notify about activity (team member actions)
   */
  async notifyActivity(
    userName: string,
    action: string,
    entityType: string,
    entityName: string
  ): Promise<boolean> {
    return this.notify({
      title: 'Team-Aktivität',
      body: `${userName} hat ${entityType} "${entityName}" ${action}`,
      category: 'activity',
      priority: 'low',
      tag: `activity-${Date.now()}`,
      silent: true, // Activity notifications are silent by default
    });
  }

  /**
   * Show system notification
   */
  async notifySystem(
    title: string,
    message: string,
    priority: NotificationPriority = 'medium'
  ): Promise<boolean> {
    return this.notify({
      title,
      body: message,
      category: 'system',
      priority,
      tag: `system-${Date.now()}`,
    });
  }

  /**
   * Show urgent alert
   */
  async notifyAlert(
    title: string,
    message: string,
    onClick?: () => void
  ): Promise<boolean> {
    return this.notify({
      title,
      body: message,
      category: 'alert',
      priority: 'urgent',
      tag: `alert-${Date.now()}`,
      requireInteraction: true,
      onClick,
    });
  }

  // ========================
  // TESTING
  // ========================

  /**
   * Send a test notification
   */
  async testNotification(): Promise<boolean> {
    return this.notify({
      title: 'Test-Benachrichtigung',
      body: 'Das Push-Notification-System funktioniert! 🎉',
      category: 'system',
      priority: 'medium',
      tag: 'test-notification',
    });
  }
}

// ========================
// EXPORTS
// ========================

export const pushNotificationService = PushNotificationService.getInstance();
export default PushNotificationService;
